import { epochSecondsToDate, z, type ZodType } from '@customerio/hydra-validation';
import type { ServicesApiSubscriptionTopic } from '@/services/api/services-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Subscription topic — sideload-only entity in this pilot. Mirrors Tag's role.
 *
 * **`id` is `string` in the entity.** The wire encodes it as a number
 * (`ServicesApiSubscriptionTopic.id: number`) but every other entity in
 * this layer uses string ids and `Campaign.subscription_topic_id` is
 * `string` on the wire too, so we coerce at the parse boundary
 * (number → string) for one consistent type across consumers.
 */

export type SubscriptionTopic = Omit<ServicesApiSubscriptionTopic, 'id' | 'created' | 'updated'> & {
  id: string;
  created: Date;
  updated: Date;
};

const SubscriptionTopicFromApi = z.looseObject({
  id: z.number().transform(String),
  created: epochSecondsToDate,
  updated: epochSecondsToDate,
}) as unknown as ZodType<SubscriptionTopic, unknown>;

/** @public */
export const subscriptionTopic = defineEntity<SubscriptionTopic, SubscriptionTopic, void, undefined, 'workspace'>({
  name: 'subscription_topic',
  scope: 'workspace',
  pk: (s) => s.id,
  schema: { read: SubscriptionTopicFromApi },
});

export const subscriptionTopicKeys = subscriptionTopic.keys;

export function parseSubscriptionTopic(raw: ServicesApiSubscriptionTopic): SubscriptionTopic {
  return subscriptionTopic.parse(raw);
}
